import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_MAX_TARGET_POINTS_TRANSFER } from '@rd-lib/constant';
import { CurrencyConversionApiService } from '@rd/core/services/currency-conversion-api/currency-conversion-api.service';
import { DateService } from '@rd/core/services/date/date.service';
import {
  CounterConfig,
  CounterTranslationsConfig,
  LoyaltyCurrency,
  LoyaltyProgram,
} from '@rd/types';
import {
  AutoRedemptionSettingStrategy,
  AutoRedemptionSettingType,
  PeriodAutoRedemptionSettingStrategyOptions,
} from '@rd/types/auto-redemption-setting.type';
import { TransferRate } from '@rd/types/transfer-rate.type';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'rd-auto-redemption-rc-specific-amount-form',
  templateUrl: './auto-redemption-specific-amount-form.component.html',
  styleUrls: ['./auto-redemption-specific-amount-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class AutoRedemptionRCSpecificAmountFormComponent
  implements OnDestroy, OnInit, OnChanges
{
  @HostBinding('class.rd-auto-redemption-tc-specific-amount-form')
  component = true;
  public readonly autoRedemptionSettingStrategy = AutoRedemptionSettingStrategy;

  @Input() homeLoyaltyCurrency: LoyaltyCurrency;
  @Input() targetLoyaltyCurrency: LoyaltyCurrency;
  @Input() targetTransferRate: TransferRate;
  @Input() loyaltyProgram: LoyaltyProgram;
  @Input() pointsAmountCounterConfig: CounterConfig;

  recurrenceOptions = [
    {
      name: 'Every month',
      value: 1,
    },
    {
      name: 'Every 3 months',
      value: 3,
    },
    {
      name: 'Every 6 months',
      value: 6,
    },
    {
      name: 'Every 12 months',
      value: 12,
    },
  ];

  options: AutoRedemptionSettingStrategy[] = [
    AutoRedemptionSettingStrategy.EarliestOpportunity,
    AutoRedemptionSettingStrategy.EveryFewMonths,
    // Add this as a possiblity for the future
    AutoRedemptionSettingStrategy.EndOfMonth,
  ];
  optionsFormControl = new UntypedFormControl(this.options[0]);
  specificAmountForm: UntypedFormGroup;
  destroy$ = new Subject();
  pointsBalanceRequired$: Observable<number>;
  startingDateFormat = 'mediumDate';

  pointsAmountCounterTranslationsConfig: CounterTranslationsConfig;

  private targetTransferRateChange = new Subject<TransferRate>();
  public formData$: BehaviorSubject<{
    autoRedemptionType: AutoRedemptionSettingType.Cash;
    displayValue: number;
    pointsRequired: number;
    strategy: AutoRedemptionSettingStrategy;
    strategyOptions: PeriodAutoRedemptionSettingStrategyOptions | {};
  } | null> = new BehaviorSubject<any | null>(null);
  public pending$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true,
  );

  constructor(
    private fb: UntypedFormBuilder,
    private dateService: DateService,
    private translateService: TranslateService,
    private currencyConversionApiService: CurrencyConversionApiService,
  ) {}

  ngOnChanges({ targetTransferRate }: SimpleChanges): void {
    if (
      !targetTransferRate.firstChange &&
      targetTransferRate.previousValue.id !== targetTransferRate.currentValue.id
    ) {
      this.targetTransferRateChange.next(targetTransferRate.currentValue);
      return;
    }
  }

  ngOnInit(): void {
    this.pointsAmountCounterTranslationsConfig = {
      hintTranslation: this.translateService.instant(
        'order.manage.auto_redemption.rc_specific_amount_form.hint_translation',
        {
          points: this.targetTransferRate.targetDenomination,
        },
      ),
      emptyErrorTranslation: '',
      patternErrorTranslation: '',
      minusButtonAriaLabel: this.translateService.instant(
        'accessibility.shared.counter.minus_button',
        {
          loyaltyCurrency: this.loyaltyProgram.shortName,
        },
      ),
      plusButtonAriaLabel: this.translateService.instant(
        'accessibility.shared.counter.plus_button',
        {
          loyaltyCurrency: this.loyaltyProgram.shortName,
        },
      ),
    };

    this.specificAmountForm = this.fb.group({
      pointsAmount: new UntypedFormControl(
        this.targetTransferRate.targetMinimumTransfer,
      ),
      recurrenceOption: new UntypedFormControl(this.recurrenceOptions[0].value),
      startingDate: new UntypedFormControl(
        this.dateService.toUTCDateWithOffset(
          this.dateService.dayjs().add(1, 'day').toDate(),
        ),
      ),
    });
    this.pointsAmountCounterConfig = {
      maxValue:
        this.targetTransferRate?.targetMaximumTransfer ||
        DEFAULT_MAX_TARGET_POINTS_TRANSFER,
      minValue: this.targetTransferRate.targetMinimumTransfer,
      valuePerChange: this.targetTransferRate.targetDenomination, // value to add for each adding or subtraction.
      formatValue: true, // whether to insert ',' or not
      aria_unit: this.targetLoyaltyCurrency.name,
    };

    this.pointsBalanceRequired$ = combineLatest([
      this.specificAmountForm.valueChanges.pipe(
        startWith(this.specificAmountForm.value),
      ),
      this.targetTransferRateChange.pipe(startWith(this.targetTransferRate)),
      this.optionsFormControl.valueChanges.pipe(
        startWith(this.optionsFormControl.value),
      ),
    ]).pipe(
      tap(() => {
        this.pending$.next(true);
      }),
      debounceTime(500),
      switchMap(([formValue, targetTransferRate, selectedOption]) => {
        // Cashback to Card dcf3c465-fd93-4255-9374-1a841c4e9e5b
        // Cashback to Account cd012acd-cdf8-4e21-9b7e-ae7f9b4fb29c
        // Home currency ef9f5a2e-4a04-40e8-9c0e-49e86a2804b7
        return this.currencyConversionApiService
          .postCurrencyConversion({
            homeCurrencyId: this.homeLoyaltyCurrency.id,
            targetCurrencyId: targetTransferRate.loyaltyCurrencyId,
            transferAmount: formValue.pointsAmount,
          })
          .pipe(
            map((val) => [
              val.redemptionAmount,
              formValue,
              targetTransferRate,
              selectedOption,
            ]),
          );
      }),
      tap((_: any) => this.pending$.next(false)),
      tap(
        ([redemptionAmount, formValue, targetTransferRate, selectedOption]: [
          number,
          any,
          TransferRate,
          string,
        ]) => {
          if (selectedOption === this.options[0]) {
            this.formData$.next({
              autoRedemptionType: AutoRedemptionSettingType.Cash,
              pointsRequired: redemptionAmount,
              displayValue: formValue.pointsAmount,
              strategy: AutoRedemptionSettingStrategy.EarliestOpportunity,
              strategyOptions: {},
            });
          }

          if (selectedOption === this.options[1]) {
            // Don't have to add threshold amount here, we just take the minimum transfer
            this.formData$.next({
              autoRedemptionType: AutoRedemptionSettingType.Cash,
              pointsRequired: targetTransferRate.minimumTransfer,
              strategy: AutoRedemptionSettingStrategy.EveryFewMonths,
              displayValue: formValue.pointsAmount,
              strategyOptions: {
                month_frequency: formValue.recurrenceOption,
                start_date: this.dateService.formatDate(
                  formValue.startingDate,
                  'yyyy-MM-dd',
                  'noTimezone',
                ),
              },
            });
          }
        },
      ),
      map(([pointsRequired]) => pointsRequired),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.pending$.next(false);
    this.pending$.complete();
    this.formData$.complete();
  }

  optionSelectedClass(option: string): string {
    return this.optionsFormControl.value === option
      ? 'typo-text-emphasis-1-mobile lg:typo-text-emphasis-1-desktop'
      : '';
  }

  optionContentClass(option: string): string {
    return this.optionsFormControl.value === option
      ? 'block-expand'
      : 'block-collapse';
  }

  startingDateChanged(newDate: Date): void {
    this.specificAmountForm.controls.startingDate.patchValue(newDate);
  }
}
